import { type FC } from "react";
import { HiMiniArrowSmallLeft, HiMiniArrowSmallRight } from "react-icons/hi2";

import { trackEvent } from "@/services/posthog.service";

import { Button } from "../_ui/button";

import { useUnsafeNavigationControls } from "./use-navigation-control-context";

export const EVENT_FORWARD_BUTTON_CLICKED = "NAVIGATION_BUTTON_FORWARD:CLICKED";
export const EVENT_BACKWARD_BUTTON_CLICKED = "NAVIGATION_BUTTON_BACKWARD:CLICKED";

interface NavigationControlButtonProps {
  type: "back" | "forward";
}

/**
 * Navigation controls component that provides back and forward buttons
 * that respect the application's navigation boundaries.
 */
export const NavigationControlButton: FC<NavigationControlButtonProps> = ({ type }) => {
  const unSafeNavigationControls = useUnsafeNavigationControls();

  const canGoBack = unSafeNavigationControls?.canGoBack ?? false;
  const canGoForward = unSafeNavigationControls?.canGoForward ?? false;

  const goBack = () => {
    unSafeNavigationControls?.goBack();
    trackEvent(EVENT_BACKWARD_BUTTON_CLICKED);
  };

  const goForward = () => {
    unSafeNavigationControls?.goForward();
    trackEvent(EVENT_FORWARD_BUTTON_CLICKED);
  };

  if (type === "back") {
    return (
      <Button
        data-testid={`navigation-control-button-${type}`}
        onClick={goBack}
        disabled={!canGoBack}
        aria-label="Go back"
        variant="ghost"
        size="icon"
        className="rounded-lg text-foreground/70"
      >
        <HiMiniArrowSmallLeft className="h-5 w-5" />
      </Button>
    );
  }
  return (
    <Button
      data-testid={`navigation-control-button-${type}`}
      onClick={goForward}
      disabled={!canGoForward}
      aria-label="Go forward"
      variant="ghost"
      size="icon"
      className="rounded-lg text-foreground/70"
    >
      <HiMiniArrowSmallRight className="h-5 w-5" />
    </Button>
  );
};
